import React from 'react';
import { ShieldCheck, SlidersHorizontal, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTranslation } from '@/components/i18n/TranslationProvider';
import { useCookieConsent } from '@/context/CookieConsentContext.jsx';

const PRIMARY_CHOICE_CLASS_NAME =
  'min-h-11 w-full border border-purple-300/40 bg-purple-500/25 text-purple-50 hover:bg-purple-500/40';

export default function CookieBanner({ visible, onManagePreferences }) {
  const { t } = useTranslation();
  const { updateConsent, version } = useCookieConsent();

  if (!visible) {
    return null;
  }

  const handleAcceptAll = () => {
    updateConsent(
      {
        preferences: true,
        analytics: true,
        marketing: true,
      },
      {
        source: 'cookie_banner_accept_all',
        channels: ['banner', 'cookie_banner'],
        metadata: { action: 'accept_all' },
      },
    );
  };

  const recordNecessaryOnly = ({ source, action }) => {
    updateConsent(
      {
        preferences: false,
        analytics: false,
        marketing: false,
      },
      {
        forceSync: true,
        source,
        channels: ['banner', 'cookie_banner'],
        metadata: { action },
      },
    );
  };

  const handleRejectAll = () => {
    recordNecessaryOnly({
      source: 'cookie_banner_reject_all',
      action: 'reject_all',
    });
  };

  const handleCloseNecessary = () => {
    recordNecessaryOnly({
      source: 'cookie_banner_close_necessary',
      action: 'close_necessary',
    });
  };

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-50 flex justify-center px-4 pb-4 sm:pb-6">
      <div
        className="pointer-events-auto relative w-full max-w-3xl rounded-[1.75rem] border border-white/10 bg-[#140d24]/95 p-4 shadow-xl backdrop-blur-md sm:p-5"
        role="region"
        aria-labelledby="cookie-banner-title"
      >
        <button
          type="button"
          className="absolute right-3 top-3 inline-flex h-11 min-h-11 w-11 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white/75 transition-colors hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-300 focus-visible:ring-offset-2 focus-visible:ring-offset-[#140d24]"
          onClick={handleCloseNecessary}
          aria-label={t('cookie.banner.close_necessary')}
        >
          <X className="h-5 w-5" aria-hidden="true" />
        </button>
        <div
          className="flex flex-col gap-4 pr-12 sm:flex-row sm:items-center sm:gap-5 sm:pr-14"
          data-testid="cookie-banner-layout"
        >
          <div className="flex flex-1 items-start gap-3">
            <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-purple-500/20 text-purple-200">
              <ShieldCheck className="h-5 w-5" aria-hidden="true" />
            </div>
            <div className="min-w-0 space-y-2">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <h2
                  id="cookie-banner-title"
                  className="text-base font-semibold text-white sm:text-lg"
                >
                  {t('cookie.banner.title')}
                </h2>
                <span className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] font-medium uppercase tracking-[0.18em] text-white/55">
                  {t('cookie.banner.version_badge', { version })}
                </span>
              </div>
              <p className="max-w-xl text-xs leading-5 text-white/68 sm:text-sm sm:leading-6">
                {t('cookie.banner.compact_description')}
              </p>
            </div>
          </div>
          <div
            className="grid w-full grid-cols-1 gap-2 sm:w-auto sm:min-w-[13rem]"
            data-testid="cookie-banner-actions"
          >
            <Button
              type="button"
              variant="secondary"
              className={PRIMARY_CHOICE_CLASS_NAME}
              onClick={handleRejectAll}
              data-consent-emphasis="primary-choice"
            >
              {t('cookie.banner.reject')}
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="min-h-11 w-full border border-white/10 bg-transparent text-white/75 hover:bg-white/10 hover:text-white"
              onClick={onManagePreferences}
              data-consent-emphasis="secondary-choice"
            >
              <span className="flex items-center justify-center gap-2">
                <SlidersHorizontal className="h-4 w-4" aria-hidden="true" />
                {t('cookie.banner.manage')}
              </span>
            </Button>
            <Button
              type="button"
              variant="secondary"
              className={PRIMARY_CHOICE_CLASS_NAME}
              onClick={handleAcceptAll}
              data-consent-emphasis="primary-choice"
            >
              {t('cookie.banner.accept')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
